import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import OtpInput from 'react-otp-input';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import getPossessiveForm from '../../helpers/possessive';
import Section from '../edu/Section';
import { DIARY_PIN_SIZE } from './index';
import FormFeedback from '../form/FormFeedback';

import './DiaryLock.scss';
import '../form/WrappedCodeInput.scss';

const DiaryLock = ({
  firstName,
  handleUnlock,
  handleOpenForgotPinModal,
  showIncorrectMessage = false
}) => {
  const { t } = useTranslation();
  
  const [pin, setPin] = useState('');
  const [localError, setLocalError] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);

  // Sync with Redux state if the container forces an error
  useEffect(() => {
    if (showIncorrectMessage) {
      setLocalError(true);
      setPin(''); 
    }
  }, [showIncorrectMessage]);

  const handleChange = (value) => {
    // Hide the error text the moment the user starts typing again
    setLocalError(false);
    setPin(value);

    // Trigger API call when the box is full
    if (value.length === DIARY_PIN_SIZE) {
      setIsUnlocking(true);
      
      const unlockPromise = handleUnlock(value);
      
      if (unlockPromise && typeof unlockPromise.then === 'function') {
        unlockPromise
          .then((response) => {
            setIsUnlocking(false);
            // Fallback: If the API returns 200 but includes an error payload
            if (response && response.error) {
              setLocalError(true);
              setPin(''); 
            }
          })
          .catch(() => {
            // The 403 Forbidden lands here. Gracefully clear PIN & show error.
            setIsUnlocking(false);
            setLocalError(true);
            setPin('');
          });
      } else {
        // Safety fallback if the action didn't return a promise
        setIsUnlocking(false);
      }
    }
  };

  const isRejected = localError && !isUnlocking;

  return (
    <div>
      <div className="page-header">
        <h1>
          {/* CRITICAL FIX: Safe fallback prevents the .slice() crash */}
          {t('diary.ownersDiary', { owner: getPossessiveForm(firstName || '') })}
        </h1>
      </div>

      <Section colorScheme="red">
        <div className="container--content">
          <div className="card diary-lock__card">
            <p className="diary-lock__intro">{t('diaryLock.pinEntry')}</p>
            
            <OtpInput
              value={pin}
              onChange={handleChange}
              numInputs={DIARY_PIN_SIZE}
              inputType="password"
              shouldAutoFocus
              // Lock the input visually while the API checks
              renderInput={(props) => <input {...props} disabled={isUnlocking} />}
              containerStyle={classNames('form-code-input', {
                'form-code-input--reject': isRejected
              })}
            />

            {isRejected && (
              <div className="diary-lock__incorrect-message">
                <FormFeedback positive={false}>
                  {t('diaryLock.incorrect')}
                </FormFeedback>
              </div>
            )}

            <p className="diary-lock__note text-note">
              {t('diaryLock.resetPinDescription')}{' '}
              <button
                type="button"
                className="diary-lock__email-link button button--link text-link"
                onClick={handleOpenForgotPinModal}
              >
                {t('diaryLock.resetPinLink')}
              </button>
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
};

DiaryLock.propTypes = {
  firstName: PropTypes.string, // Relaxed to handle initial empty renders safely
  handleUnlock: PropTypes.func.isRequired,
  handleOpenForgotPinModal: PropTypes.func.isRequired,
  showIncorrectMessage: PropTypes.bool
};

export default DiaryLock;